/**
 * Utility functions for consistent color usage across light and dark themes
 *
 * These utilities help maintain consistent color schemes throughout the application,
 * ensuring that all components use the same color combinations for positive/negative states,
 * errors, and success indicators across both light and dark modes.
 *
 * @example
 * ```tsx
 * // Usage in a component
 * import { getTrendColor } from "../utils/theme-colors";
 *
 * function MyComponent({ isPositive }: { isPositive: boolean }) {
 *   return (
 *     <Text style={{ color: getTrendColor(isPositive) }}>
 *       {isPositive ? "+5.2%" : "-2.1%"}
 *     </Text>
 *   );
 * }
 * ```
 */

/**
 * Returns the appropriate green color based on the current color scheme
 * Used for positive states, success indicators, and upward trends
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getPositiveColor(): string {
  return "light-dark(var(--mantine-color-success-6), var(--mantine-color-success-4))";
}

/**
 * Returns the appropriate red color based on the current color scheme
 * Used for negative states, error indicators, and downward trends
 *
 * @returns Color string with light-dark function for theme-aware color
 */
function getNegativeColor(): string {
  return "light-dark(var(--mantine-color-error-5), var(--mantine-color-error-1))";
}

/**
 * Returns the appropriate error color based on the current color scheme
 * Used specifically for error states and error buttons
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getErrorColor(): string {
  return "light-dark(var(--mantine-color-error-5), var(--mantine-color-error-1))";
}

/**
 * Returns the appropriate success color based on the current color scheme
 * Used for connection indicators and success states
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getSuccessColor(): string {
  return "light-dark(var(--mantine-color-success-6), var(--mantine-color-success-4))";
}

/**
 * Helper function to get color based on positive/negative state
 * Convenience function that combines getPositiveColor and getNegativeColor
 *
 * @param isPositive - Whether the state is positive (true) or negative (false)
 * @returns Color string with light-dark function for theme-aware color
 *
 * @example
 * ```tsx
 * // For displaying trend indicators
 * <Icon
 *   icon={isUp ? "trending_up" : "trending_down"}
 *   style={{ color: getTrendColor(isUp) }}
 * />
 * ```
 */
export function getTrendColor(isPositive: boolean): string {
  return isPositive ? getPositiveColor() : getNegativeColor();
}

/**
 * GREY TEXT COLOR UTILITIES
 *
 * These utilities provide consistent grey text colors across light and dark modes
 * for different text hierarchy levels and use cases.
 */

/**
 * Returns the primary text color (highest contrast)
 * Used for main headings, important text, and primary content
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getPrimaryTextColor(): string {
  return "light-dark(var(--mantine-color-black), var(--mantine-color-white))";
}

/**
 * Returns the secondary text color
 * Used for labels, captions, and less prominent text
 * Ensures WCAG AA contrast ratio of at least 4.5:1
 * Uses black in light mode to guarantee maximum contrast compliance
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getSecondaryTextColor(): string {
  // Using black in light mode to guarantee WCAG AA compliance (4.5:1 contrast ratio)
  // This ensures sufficient contrast on light backgrounds (#fdfdfd)
  return "light-dark(var(--mantine-color-black), var(--mantine-color-gray-1))";
}

/**
 * Returns the muted text color
 * Used for placeholder text, disabled states, and subtle information
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getMutedTextColor(): string {
  return "light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-4))";
}

/**
 * Returns the high contrast text color
 * Used for important readable text that needs maximum legibility
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getHighContrastTextColor(): string {
  return "light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))";
}

/**
 * Returns the border color
 * Used for separators, card borders, and visual boundaries
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getBorderColor(): string {
  return "light-dark(var(--mantine-color-gray-dark-3), var(--mantine-color-gray-dark-9))";
}

/**
 * Returns the surface background color
 * Used for card backgrounds, panel backgrounds, and elevated surfaces
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getSurfaceBackgroundColor(): string {
  return "light-dark(var(--mantine-color-gray-dark-2), var(--mantine-color-gray-dark-9))";
}

/**
 * Returns the list item background color
 * Used for list rows, selection tiles, and similar surfaces
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getListItemBackgroundColor(): string {
  return "light-dark(var(--mantine-color-gray-dark-1), var(--mantine-color-gray-dark-10))";
}

/**
 * Returns the hover background color
 * Used for interactive element hover states
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getHoverBackgroundColor(): string {
  return "light-dark(var(--mantine-color-gray-dark-2), var(--mantine-color-gray-dark-11))";
}

/**
 * Returns the page background color
 * Used for main page backgrounds and layout containers
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getPageBackgroundColor(): string {
  return "light-dark(var(--mantine-color-gray-dark-3), var(--mantine-color-gray-dark-10))";
}

/**
 * Returns the base background color for cards and components
 * Used for individual card backgrounds, component backgrounds, and base surfaces
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getBaseBackgroundColor(): string {
  return "light-dark(var(--mantine-color-gray-dark-0), var(--mantine-color-gray-dark-10))";
}

/**
 * Returns the subtle border color
 * Used for more subtle borders and dividers
 *
 * @returns Color string with light-dark function for theme-aware color
 */
export function getSubtleBorderColor(): string {
  return "light-dark(var(--mantine-color-gray-dark-3), var(--mantine-color-gray-dark-9))";
}
